import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import React from "react";

import { Card, CardHeader, CardTitle } from "~/components/ui/card";
import { Heading } from "~/components/ui/heading";
import { getSubcategoriesGroupedByCategoryWithCoaches } from "~/lib/functions/expertise";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

export const Route = createFileRoute("/_public/coaches/subcategory/")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["subcategories-grouped"],
      queryFn: () => getSubcategoriesGroupedByCategoryWithCoaches(),
    });
    return {};
  },
  head: () => {
    const breadcrumbLd = createBreadcrumbStructuredData([
      { name: "Home", url: "https://dreamcoachmatch.com" },
      { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
      {
        name: "Subcategories",
        url: "https://dreamcoachmatch.com/coaches/subcategory",
      },
    ]);
    return {
      ...createBrandMeta({
        title: "Coach Subcategories",
        description:
          "Browse coaching subcategories on Dream Coach Match to find your perfect match.",
        canonical: "https://dreamcoachmatch.com/coaches/subcategory",
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
      ],
    };
  },
  component: SubcategoriesPage,
});

function SubcategoriesPage() {
  const { data: grouped } = useQuery({
    queryKey: ["subcategories-grouped"],
    queryFn: () => getSubcategoriesGroupedByCategoryWithCoaches(),
  });

  if (!grouped) {
    return null;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <Heading
        preHeadline="Dream Coach Match"
        headline="Find coaches by subcategory"
        subheadline="Explore coaching subcategories to find your perfect match"
        isH1
      />
      <div className="mt-8 space-y-12">
        {grouped.map(({ category, subcategories }) => (
          <div key={category.id}>
            <h2 className="mb-4 font-heading text-2xl font-medium text-gray-900">
              {category.name}
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {subcategories.map((sub) => (
                <Card key={sub.id} className="hover:shadow-md">
                  <CardHeader>
                    <CardTitle>
                      <Link
                        to="/coaches/subcategory/$subcategorySlug"
                        params={{ subcategorySlug: sub.slug }}
                        className="block text-lg font-medium text-gray-900"
                      >
                        {sub.name}
                      </Link>
                    </CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
